import { useEffect, useState } from "react";

type TimeoutOptions = {
  disabled?: boolean;
  intervalMs?: number;
  resetOnPointerActivity?: boolean;
};

export default function useOnTimeout(
  timeout: number,
  callback: () => any,
  options?: TimeoutOptions
) {
  const [counter, setCounter] = useState(timeout);
  const [callbackCalled, setCallbackCalled] = useState(false);
  const isDisabled = options?.disabled ?? (process.env.NODE_ENV === "test" ? true : false);
  const intervalMs = options?.intervalMs ?? 1000;
  const resetOnPointerActivity = options?.resetOnPointerActivity ?? false;

  useEffect(() => {
    if (!resetOnPointerActivity || isDisabled) return () => undefined;

    const resetCounter = () => {
      setCounter(timeout);
      setCallbackCalled(false);
    };

    document.addEventListener("pointerdown", resetCounter);
    return () => document.removeEventListener("pointerdown", resetCounter);
  }, [isDisabled, resetOnPointerActivity, timeout]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (isDisabled) {
      return () => undefined;
    }

    if (counter > 0) {
      timer = setTimeout(() => setCounter(counter - 1), intervalMs);
    } else if (!callbackCalled) {
      callback();
      setCallbackCalled(true);
    }

    return () => clearTimeout(timer);
  }, [callback, callbackCalled, counter, intervalMs, isDisabled]);

  return counter;
}
